(function () {
  'use strict';
  if (window.OliviaCartEnhancements) return;

  class CartEnhancements {
    constructor(drawer) {
      this.drawer = drawer;
      this.cache = new Map();
      this.selections = new Map();
      this.viewed = new Set();
      this.metrics = {};
      this.requestVersion = 0;
      this.wasOpen = drawer.classList.contains('is-open');
      this.privacyReady = this.loadPrivacy();
      drawer.addEventListener('click', (event) => this.onClick(event));
      drawer.addEventListener('change', (event) => this.onChange(event));
      drawer.addEventListener('ocd:line-removed', (event) => this.onRemoved(event.detail));
      drawer.addEventListener('submit', (event) => {
        if (event.target.matches('.ocd__checkout-form') && !event.defaultPrevented && !drawer.busy) {
          this.checkoutStarted = true;
          this.track('checkout_click');
        }
      }, true);
      this.contentObserver = new MutationObserver(() => this.hydrate());
      this.contentObserver.observe(drawer.querySelector('[data-ocd-contents]'), { childList: true });
      this.rootObserver = new MutationObserver(() => {
        this.syncControls();
        var open = drawer.classList.contains('is-open');
        if (open === this.wasOpen) return;
        this.wasOpen = open;
        if (open) this.onOpen();
        else this.track('dismiss', { duration_seconds: this.elapsed() });
      });
      this.rootObserver.observe(drawer, { attributes: true, attributeFilter: ['class', 'aria-busy'] });
      window.addEventListener('pagehide', () => {
        if (drawer.isConnected && drawer.classList.contains('is-open') && !this.checkoutStarted) this.track('exit', { duration_seconds: this.elapsed() });
      });
      this.hydrate();
      if (this.wasOpen) this.onOpen();
    }

    read(selector) {
      try { return JSON.parse(this.drawer.querySelector(selector).textContent); }
      catch (error) { return {}; }
    }

    hydrate() {
      this.config = this.read('[data-ocd-tools-config]');
      this.cart = this.read('[data-ocd-cart-summary]');
      this.labels = this.config.labels || {};
      this.updateDelivery();
      this.syncControls();
      if (this.drawer.classList.contains('is-open')) this.loadRecommendation();
    }

    onOpen() {
      this.openedAt = Date.now();
      this.checkoutStarted = false;
      this.viewed.clear();
      this.track('open');
      this.loadRecommendation();
      this.observeRecommendation();
    }

    elapsed() {
      return Math.max(0, Math.round((Date.now() - (this.openedAt || Date.now())) / 1000));
    }

    route(path) {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
      return root.replace(/\/?$/, '/') + path;
    }

    eligible(products) {
      var existing = new Set((this.cart.product_ids || []).map(String));
      return (products || []).find((product) => product && !existing.has(String(product.id)) && !product.requires_selling_plan && !product.gift_card &&
        Array.isArray(product.variants) && product.variants.some((variant) => variant.available));
    }

    async recommendations(productId, intent, signal) {
      var url = new URL(this.route('recommendations/products.json'), window.location.origin);
      url.searchParams.set('product_id', String(productId));
      url.searchParams.set('limit', '6');
      url.searchParams.set('intent', intent);
      var response = await fetch(url.pathname + url.search, { headers: { Accept: 'application/json' }, signal: signal });
      if (!response.ok) throw new Error('Recommendations unavailable');
      return (await response.json()).products || [];
    }

    async loadRecommendation() {
      var section = this.drawer.querySelector('[data-ocd-upsell]');
      if (!section || !this.config.upsell || !this.cart.count) return;
      var ids = Array.from(new Set((this.cart.product_ids || []).filter((id) => Number(id) > 0))).sort();
      if (!ids.length) return;
      var manual = this.config.manual_products || [];
      var key = JSON.stringify([ids, this.cart.currency, manual]);
      var cached = this.cache.get(key);
      if (cached && Date.now() - cached.time < 60000) { this.renderRecommendation(cached.product, cached.intent); return; }
      if (this.pendingKey === key) return;
      this.pendingKey = key;
      if (this.recommendationAbort) this.recommendationAbort.abort();
      this.recommendationAbort = new AbortController();
      var version = ++this.requestVersion;
      section.hidden = true;
      try {
        var product = this.eligible(manual);
        var intent = 'complementary';
        if (!product && !this.config.manual_only) product = this.eligible(await this.recommendations(ids[0], intent, this.recommendationAbort.signal));
        if (!product && !this.config.manual_only) {
          intent = 'related';
          product = this.eligible(await this.recommendations(ids[0], intent, this.recommendationAbort.signal));
        }
        if (version !== this.requestVersion) return;
        this.cache.set(key, { time: Date.now(), product: product, intent: intent });
        this.renderRecommendation(product, intent);
      } catch (error) {
        if (version === this.requestVersion) {
          var current = this.drawer.querySelector('[data-ocd-upsell]');
          if (current) current.hidden = true;
        }
      } finally {
        if (version === this.requestVersion) this.pendingKey = null;
      }
    }

    node(tag, className, text) {
      var element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.cart.currency || 'USD' }).format(Number(cents) / 100);
    }

    renderRecommendation(product, intent) {
      var section = this.drawer.querySelector('[data-ocd-upsell]');
      if (!section) return;
      if (!product || !this.eligible([product])) { section.hidden = true; return; }
      var target = section.querySelector('[data-ocd-upsell-content]');
      section.querySelector('[data-ocd-upsell-title]').textContent = intent === 'related' ? this.labels.related_heading : this.labels.complement_heading;
      var card = this.node('div', 'ocd__upsell-card');
      card.dataset.ocdUpsellProduct = String(product.id);
      var imageUrl = product.featured_image || product.images && product.images[0];
      if (imageUrl && typeof imageUrl === 'object') imageUrl = imageUrl.src || imageUrl.url;
      if (imageUrl) {
        var image = this.node('img');
        image.src = imageUrl;
        image.alt = product.title;
        image.width = 48;
        image.height = 48;
        image.loading = 'lazy';
        card.append(image);
      }
      var main = this.node('div', 'ocd__upsell-main');
      var link = this.node('a', 'ocd__upsell-product', product.title);
      var url = new URL(product.url || '/products/' + product.handle, window.location.origin);
      link.href = url.pathname + url.search;
      main.append(link);
      var variants = product.variants.filter((variant) => variant.available);
      var controls = this.node('div', 'ocd__upsell-controls');
      var price = this.node('span', 'ocd__upsell-price');
      var add = this.node('button', 'ocd__upsell-add', this.labels.add);
      add.type = 'button';
      add.dataset.ocdAddVariant = '';
      add.dataset.productId = String(product.id);
      add.setAttribute('aria-label', this.labels.add + ' ' + product.title);
      var previous = this.selections.get(String(product.id));
      var selected = variants.find((variant) => String(variant.id) === previous) || (variants.length === 1 ? variants[0] : null);
      var cardImage = card.querySelector('img');
      if (cardImage) {
        cardImage.dataset.defaultImage = imageUrl;
        if (selected && selected.featured_image) cardImage.src = selected.featured_image;
      }
      if (variants.length > 1) {
        var select = this.node('select', 'ocd__upsell-select');
        select.dataset.ocdUpsellVariant = String(product.id);
        select.setAttribute('aria-label', this.labels.variant + ' — ' + product.title);
        var placeholder = this.node('option', '', this.labels.choose);
        placeholder.value = '';
        select.append(placeholder);
        variants.forEach((variant) => {
          var option = this.node('option', '', variant.title);
          option.value = String(variant.id);
          option.dataset.price = String(variant.price);
          option.dataset.image = variant.featured_image || imageUrl || '';
          select.append(option);
        });
        select.value = selected ? String(selected.id) : '';
        controls.append(select);
      }
      if (selected) add.dataset.ocdAddVariant = String(selected.id);
      price.textContent = this.money(selected ? selected.price : Math.min.apply(null, variants.map((variant) => Number(variant.price))));
      controls.append(price, add);
      main.append(controls);
      card.append(main);
      target.replaceChildren(card);
      section.hidden = false;
      this.syncControls();
      this.observeRecommendation();
      this.refreshFocus();
    }

    observeRecommendation() {
      if (this.impressionObserver) this.impressionObserver.disconnect();
      var card = this.drawer.querySelector('[data-ocd-upsell-product]');
      if (!card || !this.drawer.classList.contains('is-open')) return;
      if (typeof IntersectionObserver !== 'function') return;
      this.impressionObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || !this.drawer.classList.contains('is-open')) return;
        var id = card.dataset.ocdUpsellProduct;
        if (!this.viewed.has(id)) { this.viewed.add(id); this.track('upsell_view', { product_id: id }); }
      }, { root: this.drawer.querySelector('.ocd__scroll'), threshold: 0.5 });
      this.impressionObserver.observe(card);
    }

    onChange(event) {
      if (!event.target.matches('[data-ocd-upsell-variant]')) return;
      var select = event.target;
      var card = select.closest('.ocd__upsell-card');
      var option = select.options[select.selectedIndex];
      card.querySelector('[data-ocd-add-variant]').dataset.ocdAddVariant = select.value;
      if (select.value) card.querySelector('.ocd__upsell-price').textContent = this.money(option.dataset.price);
      var image = card.querySelector('img');
      if (image) image.src = option.dataset.image || image.dataset.defaultImage;
      this.selections.set(select.dataset.ocdUpsellVariant, select.value);
      this.syncControls();
    }

    onClick(event) {
      var add = event.target.closest('[data-ocd-add-variant]');
      if (add && add.dataset.ocdAddVariant) {
        event.preventDefault();
        this.addItem({ id: Number(add.dataset.ocdAddVariant), quantity: 1 }, 'upsell_add', { product_id: add.dataset.productId, variant_id: add.dataset.ocdAddVariant });
      }
      if (event.target.closest('[data-ocd-restore]')) { event.preventDefault(); this.restore(); }
      if (event.target.closest('[data-ocd-dismiss-undo]')) this.hideUndo();
      var product = event.target.closest('.ocd__upsell-product');
      if (product) this.track('upsell_product_click', { product_id: product.closest('.ocd__upsell-card').dataset.ocdUpsellProduct });
    }

    async addItem(item, action, metric) {
      if (this.drawer.busy) return false;
      this.drawer.setBusy(true);
      var error = this.drawer.querySelector('[data-ocd-upsell-error]');
      if (error) error.hidden = true;
      try {
        var response = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item], sections: this.drawer.getSectionsToRender().map((section) => section.id), sections_url: window.location.pathname }),
        });
        var state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || this.labels.error);
        if (state.sections && state.sections['olivia-cart-drawer']) this.drawer.renderFromState(state);
        else { await this.drawer.refresh(); this.drawer.publish(state); }
        this.hydrate();
        this.track(action, metric);
        return true;
      } catch (problem) {
        this.drawer.showError(null, problem.message || this.labels.error);
        var toast = this.drawer.querySelector('[data-ocd-undo-text]');
        if (action === 'restore' && toast) toast.textContent = problem.message || this.labels.error;
        return false;
      } finally { this.drawer.setBusy(false); this.syncControls(); }
    }

    onRemoved(snapshot) {
      if (!snapshot || !Number(snapshot.id) || !Number(snapshot.quantity)) return;
      this.hydrate();
      this.track('remove', { product_id: snapshot.product_id, variant_id: snapshot.id, quantity: snapshot.quantity });
      if (!this.config.undo) return;
      this.removed = snapshot;
      this.undoExpires = Date.now() + 15000;
      window.clearTimeout(this.undoTimeout);
      var toast = this.drawer.querySelector('[data-ocd-undo]');
      if (!toast) return;
      toast.querySelector('[data-ocd-undo-text]').textContent = this.labels.removed + ' ' + snapshot.title;
      toast.hidden = false;
      this.syncControls();
      this.refreshFocus();
      this.undoTimeout = window.setTimeout(() => this.hideUndo(), 15000);
    }

    async restore() {
      if (!this.removed || Date.now() >= this.undoExpires || this.drawer.busy) return;
      var snapshot = this.removed;
      var properties = Array.isArray(snapshot.properties) ? Object.fromEntries(snapshot.properties) : snapshot.properties || {};
      var item = { id: Number(snapshot.id), quantity: Number(snapshot.quantity), properties: properties };
      if (snapshot.selling_plan) item.selling_plan = snapshot.selling_plan;
      if (await this.addItem(item, 'restore', { product_id: snapshot.product_id, variant_id: snapshot.id, quantity: snapshot.quantity })) this.hideUndo();
    }

    hideUndo() {
      window.clearTimeout(this.undoTimeout);
      this.removed = null;
      var toast = this.drawer.querySelector('[data-ocd-undo]');
      if (toast) toast.hidden = true;
      this.refreshFocus();
    }

    syncControls() {
      this.drawer.querySelectorAll('[data-ocd-add-variant]').forEach((button) => { button.disabled = this.drawer.busy || !button.dataset.ocdAddVariant; });
      var restore = this.drawer.querySelector('[data-ocd-restore]');
      if (restore) restore.disabled = this.drawer.busy || !this.removed || Date.now() >= this.undoExpires;
    }

    refreshFocus() {
      if (!this.drawer.classList.contains('is-open') || typeof trapFocus !== 'function') return;
      var panel = this.drawer.querySelector('.ocd__panel');
      var active = document.activeElement;
      if (!panel.contains(active) || active.closest('[hidden]')) active = this.drawer.querySelector('[data-ocd-close]');
      trapFocus(panel, active);
    }

    deliveryWindow(now) {
      var min = Math.max(0, Number(this.config.processing_min) || 0) + Math.max(0, Number(this.config.shipping_min) || 0);
      var max = Math.max(min, Math.max(0, Number(this.config.processing_max) || 0) + Math.max(0, Number(this.config.shipping_max) || 0));
      var parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23' }).formatToParts(now || new Date());
      var values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
      var base = new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day), 12));
      var weekend = [0, 6].includes(base.getUTCDay());
      if (weekend || Number(values.hour) >= 13) {
        do { base.setUTCDate(base.getUTCDate() + 1); } while ([0, 6].includes(base.getUTCDay()));
      }
      var add = (days) => {
        var date = new Date(base);
        for (var remaining = days; remaining > 0;) {
          date.setUTCDate(date.getUTCDate() + 1);
          if (![0, 6].includes(date.getUTCDay())) remaining--;
        }
        return date;
      };
      return { min: min, max: max, from: add(min), to: add(max) };
    }

    updateDelivery() {
      var element = this.drawer.querySelector('[data-ocd-delivery]');
      if (!element) return;
      element.hidden = !this.config.delivery || this.cart.requires_shipping === false;
      if (element.hidden) return;
      var text = element.querySelector('[data-ocd-delivery-text]');
      if (this.config.country !== 'US') { text.textContent = this.labels.delivery_elsewhere; return; }
      var range = this.deliveryWindow();
      var format = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      text.textContent = this.labels.delivery_prefix + ': ' + format.format(range.from) + ' – ' + format.format(range.to);
      element.title = this.labels.delivery_range.replace('[min]', range.min).replace('[max]', range.max);
    }

    loadPrivacy() {
      if (window.Shopify && window.Shopify.customerPrivacy) return Promise.resolve();
      return new Promise((resolve) => {
        if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => resolve());
        } else resolve();
      });
    }

    track(action, detail) {
      if (!this.config || !this.config.analytics) return;
      this.cart = this.read('[data-ocd-cart-summary]');
      var data = Object.assign({ cart_quantity: Number(this.cart.count) || 0, cart_value: (Number(this.cart.total) || 0) / 100, currency: this.cart.currency || 'USD' }, detail || {});
      var name = 'om_cart_' + action;
      this.metrics[name] = (this.metrics[name] || 0) + 1;
      this.drawer.dataset.ocdMetrics = JSON.stringify(this.metrics);
      this.drawer.dataset.ocdLastMetric = JSON.stringify({ event: name, data: data });
      this.drawer.dispatchEvent(new CustomEvent('ocd:metric', { detail: { event: name, data: data } }));
      var theme = window.Shopify && window.Shopify.theme;
      if (!theme || theme.role !== 'main') return;
      this.privacyReady.then(() => {
        try {
          var privacy = window.Shopify && window.Shopify.customerPrivacy;
          if (!privacy || !privacy.analyticsProcessingAllowed()) return;
          if (typeof window.gtag === 'function') window.gtag('event', name, data);
          else if (window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish('olivia_mary:' + name, data);
        } catch (error) {}
      });
    }
  }

  window.OliviaCartEnhancements = CartEnhancements;
  var initialize = () => {
    var drawer = document.querySelector('#OliviaCartDrawer');
    if (drawer && !drawer.ocdExtras && typeof drawer.renderFromState === 'function') drawer.ocdExtras = new CartEnhancements(drawer);
  };
  document.addEventListener('shopify:section:load', initialize);
  customElements.whenDefined('cart-drawer').then(() => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
  });
})();
